import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydModal from '../../commonDesign/SydModal'
import SydModalActions from '../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../commonDesign/Button'
import { useSaveViewForClient } from './hooks/useSaveViewForClient'

const useStyles = makeStyles(() => ({
  field: {
    marginBottom: 16
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    border: 'var(--border-neutral-thin, 1px solid #ddd)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    background: 'var(--theme-color-surface-d0, #fff)',
    color: 'inherit',
    outline: 'none',
    '&:focus': {
      borderColor: 'var(--color-primary-500, #1976d2)',
      outline: '1px solid var(--color-primary-500, #1976d2)'
    }
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--color-neutral-600, #666)',
    marginBottom: 6,
    display: 'block'
  },
  errorMessage: {
    marginTop: 8,
    fontSize: 12,
    color: 'var(--color-danger-600, #c62828)'
  }
}))

/**
 * Two-step save: create the explorer configuration as a firm-wide row,
 * then assign it to the current client. If the assign call fails we leave
 * the saved (global) row behind so the user can recover from the picker
 * without re-typing the name.
 */
export default function SaveViewForClientDialog ({
  open,
  onClose,
  clientId,
  clientName,
  serializeConfig,
  onSaveComplete
}) {
  const classes = useStyles()
  const [name, setName] = useState('')
  const [errorMessage, setErrorMessage] = useState(null)
  // New views always land at the bottom of the client's section (the
  // `useSaveViewForClient` hook defaults to `makeDefault: false`, which
  // omits `ordinal` and lets the backend assign MAX(existing) + 10).
  // Reordering -- including "make this the default" -- is done from the
  // Open View dialog via drag, not exposed here, to keep the save form
  // a single decision.
  const { saveAndAssign, isWorking } = useSaveViewForClient(clientId)

  useEffect(() => {
    if (open) {
      setName('')
      setErrorMessage(null)
    }
  }, [open])

  const handleSave = useCallback(async () => {
    const trimmed = name.trim()
    if (!trimmed || !clientId || isWorking) return
    setErrorMessage(null)
    try {
      const result = await saveAndAssign({ name: trimmed, makeDefault: false, serializeConfig })
      onSaveComplete(result)
      onClose()
    } catch (err) {
      // Either the save or the assign failed. Surface the failure inline
      // so the user knows the "Save" button stopped processing for a real
      // reason; the dialog stays open with the typed name intact for retry.
      // If the failure was on the assign step the saved (global) view is
      // left in place; the user can recover by closing this dialog and
      // clicking "Save for [client]" on the row in the Open View dialog
      // without re-typing the name.
      console.error('Save View for Client failed', err)
      setErrorMessage(err?.message || 'Save failed. Please try again.')
    }
  }, [name, clientId, isWorking, saveAndAssign, serializeConfig, onSaveComplete, onClose])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleSave()
  }, [handleSave])

  const title = clientName ? `Save View for ${clientName}` : 'Save View for Client'

  return (
    <SydModal open={open} onClose={onClose} title={title} size='small'>
      <div className={classes.field}>
        <label className={classes.label}>View Name</label>
        <input
          className={classes.input}
          type='text'
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder='Quarterly Review'
        />
        {errorMessage ? (
          <div className={classes.errorMessage} role='alert'>{errorMessage}</div>
        ) : null}
      </div>
      <SydModalActions>
        <SydButton variant='outline' size='sm' onClick={onClose} disabled={isWorking}>Cancel</SydButton>
        <SydButton
          variant='primary'
          size='sm'
          onClick={handleSave}
          disabled={!name.trim() || !clientId || isWorking}
          processing={isWorking}
        >
          Save
        </SydButton>
      </SydModalActions>
    </SydModal>
  )
}

SaveViewForClientDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientId: PropTypes.number,
  clientName: PropTypes.string,
  serializeConfig: PropTypes.func.isRequired,
  onSaveComplete: PropTypes.func.isRequired
}
